import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { DateField, DropdownField } from './FormFields';
import {
  createEpisode,
  getAvailableClinicians,
  listAgencyClinicPhysicians,
  listPatientInsurances,
} from '../services/api';
import type {
  AvailableClinicianResponse,
  ClinicPhysicianResponse,
  EpisodeSummaryResponse,
  PatientInsuranceResponse,
} from '../services/api/types.gen';
import { useToast } from '../hooks/useToastContext';
import {
  formatDateToISO,
  getBusinessToday,
  parseISOToLocalDate,
} from '../utils/dateUtils';
import {
  parse4xxApiError,
  parse4xxHeyApiResult,
} from '../utils/apiErrorDetail';
import { cliniciansToSelectOptions } from '../utils/clinicianUtils';
import { UnclaimedRecordMarker } from './imported/UnclaimedRecordMarker';
import { unclaimedClinicianCopy } from './clinician/unclaimedClinicianCopy';
import { formatPhysicianName } from '../utils/formatting';
import { patientInsuranceSectionPath } from '../utils/navigation';
import { MAX_EPISODE_END_DATE_OFFSET } from '../utils/episodeBoundaryVisits';

type DisabledDateRange = { from: Date; to: Date };

type Props = {
  open: boolean;
  onClose: () => void;
  patientId: string;
  clinicId: string;
  previousEpisode: EpisodeSummaryResponse | null;
  existingEpisodes: EpisodeSummaryResponse[];
  onCreated: (newEpisode: EpisodeSummaryResponse) => void;
};

function isoAddDays(iso: string, days: number): string {
  const date = parseISOToLocalDate(iso);
  if (!date) return iso;
  date.setDate(date.getDate() + days);
  return formatDateToISO(date);
}

function initialStartDate(previousEpisode: EpisodeSummaryResponse | null) {
  return previousEpisode
    ? isoAddDays(previousEpisode.end_date, 1)
    : getBusinessToday();
}

function isoToDate(iso: string): Date {
  return parseISOToLocalDate(iso) ?? new Date(`${iso}T00:00:00`);
}

function maxEpisodeEndDate(startDate: string): string {
  return isoAddDays(startDate, MAX_EPISODE_END_DATE_OFFSET);
}

function patientInsuranceLabel(insurance: PatientInsuranceResponse): string {
  if (insurance.plan_name) {
    return `${insurance.insurance_name} — ${insurance.plan_name}`;
  }
  return insurance.insurance_name;
}

/**
 * The care team the previous episode holds, when the picker cannot show them
 * (EMR-1728).
 *
 * The selects list staff who can take new work at this clinic. The previous
 * episode's assignee may not be on that list — an unclaimed row a Kinnser
 * import created, or somebody whose access or role has since changed. Either
 * way the modal used to seed the id, render nothing, and submit the id anyway,
 * so the clinician got an error about a field that looked empty.
 *
 * So a seeded id the list cannot show becomes an option of its own, named and
 * marked. Clearing it instead would drop the care team silently and misstate
 * what the previous episode held.
 */
type CarriedStaff = {
  id: string;
  label: string;
  /** An imported row nobody has signed in to, which Roger accepts (EMR-1728). */
  unclaimed: boolean;
};

function carriedStaff(
  id: string | null | undefined,
  name: string | null | undefined,
  unclaimed: boolean | null | undefined
): CarriedStaff | null {
  if (!id) return null;
  // The summary hands over one assembled name, not the parts
  // `getClinicianDisplayName` needs, so no credentials or titles are appended
  // here. An imported row carries none of those anyway.
  const fallback = unclaimed ? 'Imported staff member' : 'Previous assignee';
  return { id, label: name?.trim() || fallback, unclaimed: Boolean(unclaimed) };
}

/**
 * The carried person only when the picker cannot offer them. Returns null
 * until the picker has loaded, so an in-flight list never looks empty.
 */
function carriedStaffToShow(
  carried: CarriedStaff | null,
  options: { value: string }[],
  optionsLoaded: boolean
): CarriedStaff | null {
  if (!carried || !optionsLoaded) return null;
  if (options.some(option => option.value === carried.id)) return null;
  return carried;
}

/** Put the carried person first, then the claimed staff alphabetically. */
function carriedFirstComparator(carried: CarriedStaff | null) {
  return (
    a: { value: string; label: string },
    b: { value: string; label: string }
  ) => {
    if (carried) {
      if (a.value === carried.id) return -1;
      if (b.value === carried.id) return 1;
    }
    return a.label.localeCompare(b.label);
  };
}

function withCarriedOption(
  options: { value: string; label: string; user_id: string | null }[],
  carried: CarriedStaff | null
) {
  if (!carried) return options;
  return [
    { value: carried.id, label: carried.label, user_id: null },
    ...options,
  ];
}

const UNAVAILABLE_COPY = [
  'This person is no longer available at this clinic.',
  'Their access or their role changed.',
  'Choose someone else before saving.',
];

/**
 * The chart's amber treatment, applied to the field rather than to the option:
 * a Radix select trigger is itself a button, so the marker's own button cannot
 * live inside it.
 *
 * An unclaimed import reads the chart's shared `carry` copy — Roger keeps that
 * assignment. Anyone else the picker cannot offer is genuinely unavailable and
 * says so, because saving them will be refused.
 */
function CarriedStaffMarker({
  carried,
  testId,
}: {
  carried: CarriedStaff;
  testId: string;
}) {
  const carryCopy = unclaimedClinicianCopy('carry', carried.label);
  const popoverLabel = carried.unclaimed
    ? carryCopy.popoverLabel
    : `Why ${carried.label} cannot be kept`;
  const lines = carried.unclaimed ? carryCopy.lines : UNAVAILABLE_COPY;

  return (
    <UnclaimedRecordMarker
      popoverLabel={popoverLabel}
      lines={lines}
      className='whitespace-nowrap text-xs'
      testId={testId}
    >
      {carried.unclaimed ? 'Imported' : 'Unavailable'}
    </UnclaimedRecordMarker>
  );
}

function episodeRangeToMatcher(
  episode: EpisodeSummaryResponse
): DisabledDateRange | null {
  const from = parseISOToLocalDate(episode.start_date);
  const to = parseISOToLocalDate(episode.end_date);
  if (!from || !to) return null;
  return { from, to };
}

export default function AddEpisodeModal({
  open,
  onClose,
  patientId,
  clinicId,
  previousEpisode,
  existingEpisodes,
  onCreated,
}: Props) {
  const navigate = useNavigate();
  const { error: showError } = useToast();
  const [startDate, setStartDate] = useState(() =>
    initialStartDate(previousEpisode)
  );
  const [endDate, setEndDate] = useState(() => maxEpisodeEndDate(startDate));
  const [patientInsuranceId, setPatientInsuranceId] = useState<string | null>(
    previousEpisode?.patient_insurance_id ?? null
  );
  const [assignedClinicianId, setAssignedClinicianId] = useState<string | null>(
    previousEpisode?.assigned_clinician_id ?? null
  );
  const [caseManagerId, setCaseManagerId] = useState<string | null>(
    previousEpisode?.case_manager_id ?? null
  );
  const [clinicPhysicianId, setClinicPhysicianId] = useState<string | null>(
    previousEpisode?.clinic_physician_id ?? null
  );
  const [completePrevious, setCompletePrevious] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [insurances, setInsurances] = useState<PatientInsuranceResponse[]>([]);
  const [insurancesLoading, setInsurancesLoading] = useState(false);
  const [clinicians, setClinicians] = useState<AvailableClinicianResponse[]>(
    []
  );
  const [physicians, setPhysicians] = useState<ClinicPhysicianResponse[]>([]);
  // The carried option is only injected once the picker has answered; before
  // then every id looks absent from an empty list.
  const [cliniciansLoaded, setCliniciansLoaded] = useState(false);

  useEffect(() => {
    if (!open) return;

    const nextStart = initialStartDate(previousEpisode);
    setStartDate(nextStart);
    setEndDate(maxEpisodeEndDate(nextStart));
    setPatientInsuranceId(previousEpisode?.patient_insurance_id ?? null);
    setAssignedClinicianId(previousEpisode?.assigned_clinician_id ?? null);
    setCaseManagerId(previousEpisode?.case_manager_id ?? null);
    setClinicPhysicianId(previousEpisode?.clinic_physician_id ?? null);
    setCompletePrevious(true);
    setCliniciansLoaded(false);
  }, [open, previousEpisode]);

  useEffect(() => {
    if (!open || !patientId) return;

    let cancelled = false;
    const loadPatientInsurances = async () => {
      setInsurancesLoading(true);
      try {
        const insuranceResponse = await listPatientInsurances({
          path: { patient_id: patientId },
        });

        if (cancelled) return;
        setInsurances(insuranceResponse.data ?? []);
      } catch (error) {
        console.error('Failed to load patient insurances:', error);
        if (!cancelled) {
          setInsurances([]);
        }
      } finally {
        if (!cancelled) {
          setInsurancesLoading(false);
        }
      }
    };

    void loadPatientInsurances();

    return () => {
      cancelled = true;
    };
  }, [patientId, open]);

  useEffect(() => {
    if (!open || !clinicId) return;

    let cancelled = false;
    const loadClinicOptions = async () => {
      try {
        const [clinicianResponse, physicianResponse] = await Promise.all([
          getAvailableClinicians({
            query: { clinic_id: clinicId },
          }),
          listAgencyClinicPhysicians({
            path: { agency_clinic_id: clinicId },
          }),
        ]);

        if (cancelled) return;
        setClinicians(clinicianResponse.data ?? []);
        setPhysicians(physicianResponse.data ?? []);
        setCliniciansLoaded(true);
      } catch (error) {
        console.error('Failed to load add episode options:', error);
        if (!cancelled) {
          setClinicians([]);
          setPhysicians([]);
          setCliniciansLoaded(true);
        }
      }
    };

    void loadClinicOptions();

    return () => {
      cancelled = true;
    };
  }, [clinicId, open]);

  const showCompletePrevious =
    previousEpisode !== null &&
    previousEpisode.status === 'active' &&
    previousEpisode.end_date < startDate;

  // Cancelled ("Inactive") episodes are retired and must not block a replacement
  // episode over the same dates (EMR-158). Filter once here so the disabled-date
  // pickers and the overlap check all agree.
  const activeExistingEpisodes = useMemo(
    () => existingEpisodes.filter(episode => episode.status !== 'cancelled'),
    [existingEpisodes]
  );

  const existingEpisodeDateRanges = useMemo(
    () =>
      activeExistingEpisodes
        .map(episodeRangeToMatcher)
        .filter((range): range is DisabledDateRange => range !== null),
    [activeExistingEpisodes]
  );
  const minEndDate = isoAddDays(startDate, 1);
  const maxEndDate = maxEpisodeEndDate(startDate);
  const endDateDisabledRanges = useMemo(() => {
    const minEnd = minEndDate;
    const maxEnd = maxEndDate;

    return activeExistingEpisodes
      .map(episode => {
        if (episode.end_date < minEnd || episode.start_date > maxEnd) {
          return null;
        }

        const from = isoToDate(
          episode.start_date > minEnd ? episode.start_date : minEnd
        );
        const to = isoToDate(
          episode.end_date < maxEnd ? episode.end_date : maxEnd
        );

        return { from, to };
      })
      .filter((range): range is { from: Date; to: Date } => range !== null);
  }, [activeExistingEpisodes, maxEndDate, minEndDate]);

  const overlap = useMemo(
    () =>
      activeExistingEpisodes.find(
        episode =>
          startDate <= episode.end_date && endDate >= episode.start_date
      ),
    [endDate, activeExistingEpisodes, startDate]
  );

  const dateOrderInvalid = endDate <= startDate;
  const episodeTooLong = endDate > maxEndDate;
  const hasPatientInsurances = insurances.length > 0;
  const insuranceNotSelected = hasPatientInsurances && !patientInsuranceId;
  const submitDisabled =
    submitting ||
    insurancesLoading ||
    !hasPatientInsurances ||
    insuranceNotSelected ||
    dateOrderInvalid ||
    episodeTooLong ||
    Boolean(overlap);

  const handleGoToPatientInsurance = () => {
    onClose();
    navigate(patientInsuranceSectionPath(patientId));
  };

  const handleStartDateChange = (value: string | null) => {
    if (!value) return;
    setStartDate(value);
    setEndDate(maxEpisodeEndDate(value));
  };

  const handleSubmit = async () => {
    if (submitDisabled) return;

    setSubmitting(true);
    try {
      const result = await createEpisode({
        body: {
          patient_id: patientId,
          start_date: startDate,
          end_date: endDate,
          patient_insurance_id: patientInsuranceId,
          assigned_clinician_id: assignedClinicianId,
          case_manager_id: caseManagerId,
          clinic_physician_id: clinicPhysicianId,
          complete_previous_episode: showCompletePrevious && completePrevious,
        },
        throwOnError: false,
      });

      if (!result.data) {
        const { message } = parse4xxHeyApiResult(result);
        showError(message ?? 'Failed to add episode');
        return;
      }

      onCreated(result.data);
      onClose();
    } catch (error) {
      console.error('Failed to add episode:', error);
      const { message } = parse4xxApiError(error);
      showError(message ?? 'Failed to add episode');
    } finally {
      setSubmitting(false);
    }
  };

  // Memoized because the design system's DropdownField memoizes its sort on
  // `[options, sortOptions]`; new identities every render would re-sort every
  // render.
  const claimedClinicianOptions = useMemo(
    () => cliniciansToSelectOptions(clinicians, 'clinician'),
    [clinicians]
  );
  const claimedCaseManagerOptions = useMemo(
    () => cliniciansToSelectOptions(clinicians, 'caseManager'),
    [clinicians]
  );

  const carriedClinician = useMemo(
    () =>
      carriedStaffToShow(
        carriedStaff(
          previousEpisode?.assigned_clinician_id,
          previousEpisode?.assigned_clinician_name,
          previousEpisode?.assigned_clinician_unclaimed_import
        ),
        claimedClinicianOptions,
        cliniciansLoaded
      ),
    [previousEpisode, claimedClinicianOptions, cliniciansLoaded]
  );
  const carriedCaseManager = useMemo(
    () =>
      carriedStaffToShow(
        carriedStaff(
          previousEpisode?.case_manager_id,
          previousEpisode?.case_manager_name,
          previousEpisode?.case_manager_unclaimed_import
        ),
        claimedCaseManagerOptions,
        cliniciansLoaded
      ),
    [previousEpisode, claimedCaseManagerOptions, cliniciansLoaded]
  );

  const clinicianOptions = useMemo(
    () => withCarriedOption(claimedClinicianOptions, carriedClinician),
    [claimedClinicianOptions, carriedClinician]
  );
  const caseManagerOptions = useMemo(
    () => withCarriedOption(claimedCaseManagerOptions, carriedCaseManager),
    [claimedCaseManagerOptions, carriedCaseManager]
  );
  const clinicianSort = useMemo(
    () => carriedFirstComparator(carriedClinician),
    [carriedClinician]
  );
  const caseManagerSort = useMemo(
    () => carriedFirstComparator(carriedCaseManager),
    [carriedCaseManager]
  );
  const physicianOptions = physicians.map(physician => ({
    value: physician.id,
    label: formatPhysicianName(physician),
  }));

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>Add Episode</DialogTitle>
          <DialogDescription className='sr-only'>
            Create a new episode for this patient.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-2'>
          <div>
            <DateField
              label='Start date'
              htmlFor='add-episode-start-date'
              data-testid='add-episode-start-date'
              value={startDate}
              onChange={handleStartDateChange}
              fieldSize='default'
              disabledDates={existingEpisodeDateRanges}
            />
          </div>

          <div>
            <DateField
              label='End date'
              htmlFor='add-episode-end-date'
              data-testid='add-episode-end-date'
              value={endDate}
              onChange={(value: string | null) => {
                if (!value) return;
                setEndDate(value);
              }}
              fieldSize='default'
              minDate={isoToDate(minEndDate)}
              maxDate={isoToDate(maxEndDate)}
              disabledDates={endDateDisabledRanges}
            />
            {dateOrderInvalid && (
              <p className='mt-1 text-sm text-destructive'>
                End date must be after start date.
              </p>
            )}
            {episodeTooLong && (
              <p className='mt-1 text-sm text-destructive'>
                Episode cannot be longer than 60 days.
              </p>
            )}
          </div>

          {insurancesLoading ? (
            <div>
              <Label htmlFor='add-episode-insurance'>Insurance</Label>
              <p className='mt-1 text-sm text-muted-foreground'>
                Loading insurances…
              </p>
            </div>
          ) : hasPatientInsurances ? (
            <DropdownField
              label='Insurance'
              htmlFor='add-episode-insurance'
              variant='formSelect'
              sortOptions
              placeholder='None'
              options={insurances.map(insurance => ({
                value: insurance.id,
                label: patientInsuranceLabel(insurance),
              }))}
              value={patientInsuranceId ?? ''}
              onChange={value => setPatientInsuranceId(value || null)}
            />
          ) : (
            <div>
              <Label>Insurance</Label>
              <button
                type='button'
                onClick={handleGoToPatientInsurance}
                className='mt-1 text-sm text-primary hover:underline text-left'
              >
                Add an insurance entry for this patient before you can create a
                new episode.
              </button>
            </div>
          )}

          <DropdownField
            label='Assigned clinician'
            htmlFor='add-episode-assigned-clinician'
            variant='formSelect'
            sortOptions={clinicianSort}
            placeholder='Unassigned'
            options={clinicianOptions}
            value={assignedClinicianId ?? ''}
            onChange={value => setAssignedClinicianId(value || null)}
            extra={
              carriedClinician &&
              assignedClinicianId === carriedClinician.id ? (
                <CarriedStaffMarker
                  carried={carriedClinician}
                  testId='add-episode-assigned-clinician-unclaimed'
                />
              ) : undefined
            }
          />

          <DropdownField
            label='Case manager'
            htmlFor='add-episode-case-manager'
            variant='formSelect'
            sortOptions={caseManagerSort}
            placeholder='Unassigned'
            options={caseManagerOptions}
            value={caseManagerId ?? ''}
            onChange={value => setCaseManagerId(value || null)}
            extra={
              carriedCaseManager && caseManagerId === carriedCaseManager.id ? (
                <CarriedStaffMarker
                  carried={carriedCaseManager}
                  testId='add-episode-case-manager-unclaimed'
                />
              ) : undefined
            }
          />

          <DropdownField
            label='Physician'
            htmlFor='add-episode-physician'
            variant='formSelect'
            sortOptions
            placeholder='None'
            options={physicianOptions}
            value={clinicPhysicianId ?? ''}
            onChange={value => setClinicPhysicianId(value || null)}
          />

          {showCompletePrevious && (
            <div className='flex items-center gap-2'>
              <Checkbox
                id='ae-complete-previous'
                checked={completePrevious}
                onCheckedChange={value => setCompletePrevious(Boolean(value))}
              />
              <Label htmlFor='ae-complete-previous'>
                Mark previous episode as completed
              </Label>
            </div>
          )}

          {overlap && (
            <p className='text-sm text-destructive'>
              Overlaps episode {overlap.start_date} - {overlap.end_date}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          {insuranceNotSelected ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-flex'>
                    <Button onClick={handleSubmit} disabled>
                      {submitting ? 'Adding...' : 'Add Episode'}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>An insurance must be selected</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Button onClick={handleSubmit} disabled={submitDisabled}>
              {submitting ? 'Adding...' : 'Add Episode'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
